import { useEffect, useState } from 'react';
import { searchLocations, type LocationSuggestion } from '../services/neighborhoodMapService';

/**
 * Debounced city/district autocomplete against /locations/search.
 * Extracted from components/search/LocationSearch so the home-hero's unified
 * search input and the standalone LocationSearch field share one lookup
 * (250ms debounce, cancellation flag against out-of-order responses).
 */
export function useLocationSuggestions(term: string, cityId?: string): {
  locations: LocationSuggestion[];
  loadingLocations: boolean;
} {
  const [locations, setLocations] = useState<LocationSuggestion[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  useEffect(() => {
    const trimmed = term.trim();
    if (trimmed.length < 1) {
      setLocations([]);
      setLoadingLocations(false);
      return;
    }
    let cancelled = false;
    setLoadingLocations(true);
    const handle = setTimeout(async () => {
      try {
        const data = await searchLocations(trimmed, cityId);
        if (!cancelled) setLocations(data);
      } catch {
        if (!cancelled) setLocations([]);
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [term, cityId]);

  return { locations, loadingLocations };
}
